import { useFormikContext } from 'formik';
import { ChangeEvent, ReactElement } from 'react';
import { object, reach } from 'yup';
import { NeglectedOrDelinquentInformationFormValues } from '../../../types/NeglectedOrDelinquentInformationModel';
import { DIPLOMA_FIELDS } from '../../../types/NeglectedOrDelinquentHighSchoolDiplomaFields';
import { PanelType } from '../../../types/PanelType';
import { YES_NO_OPTIONS } from '../../../types/Constants';
import { isValidNeglectedOrDelinquentCount } from '../../../utilities/neglectedOrDelinquentUtilities';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';
import FormRadioList from '../../FormInputs/FormRadioList/FormRadioList';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

const question =
    'Did the LEA award a high school diploma to students being served in the ' +
    'program during the selected school year?';
const NeglectedOrDelinquentHighSchoolDiplomaPanel = ({
    disabled,
    validationSchema,
}: {
    disabled: boolean;
    validationSchema: ReturnType<
        typeof createNeglectedOrDelinquentInformationValidationSchema
    >;
}): ReactElement => {
    const { values, errors, setFieldValue, setFieldTouched } =
        useFormikContext<NeglectedOrDelinquentInformationFormValues>();
    const diploma = values.highSchoolDiploma;
    const showCounts = diploma.awardedHighSchoolDiploma === 'Yes';
    const awardedField = 'highSchoolDiploma.awardedHighSchoolDiploma';
    const radioSchema = object().shape({
        [awardedField]: reach(validationSchema, awardedField),
    });
    const canCalculate = DIPLOMA_FIELDS.every(({ key }) =>
        isValidNeglectedOrDelinquentCount(diploma[key]));
    const total = canCalculate
        ? DIPLOMA_FIELDS.reduce((sum, { key }) => sum + Number(diploma[key]), 0)
        : '';

    const handleAwardedClick = (event: ChangeEvent<HTMLButtonElement>): void => {
        if (event.target.value === 'No') {
            DIPLOMA_FIELDS.forEach(({ key }) => {
                const field = `highSchoolDiploma.${key}`;
                setFieldValue(field, '', false);
                setFieldTouched(field, false, false);
            });
        }
    };

    return (
        <Panel panelType={PanelType.INFO} heading="High School Diploma">
            <p>
                Provide information about students who obtained a high school diploma
                during or after participation in the program.
            </p>
            <FormRadioList
                field={awardedField}
                displayName={question}
                options={YES_NO_OPTIONS}
                optionOrientation={FormRadioListOrientation.Horizontal}
                disabled={disabled}
                validationSchema={radioSchema}
                onClick={handleAwardedClick}
            />
            {showCounts && (
                <section>
                    <p>
                        Provide the <strong>number of students</strong> awarded a high
                        school diploma:
                    </p>
                    <div className="nord-high-school-diploma">
                        {DIPLOMA_FIELDS.map(({ key, label }) => {
                            const field = `highSchoolDiploma.${key}`;
                            const inputSchema = object().shape({
                                [field]: reach(validationSchema, field).resolve({
                                    parent: diploma,
                                    value: diploma[key],
                                }),
                            });
                            return (
                                <FormTextbox
                                    key={key}
                                    field={field}
                                    displayName={label}
                                    disabled={disabled}
                                    validationSchema={inputSchema}
                                />
                            );
                        })}
                        <div className="field-container disabled">
                            <FormInputLabel
                                field="totalStudentsAwardedDiplomas"
                                displayName="Total Students Awarded Diplomas"
                                disabled
                            />
                            <input
                                id="totalStudentsAwardedDiplomas"
                                className="data-input"
                                type="text"
                                disabled
                                readOnly
                                value={total}
                            />
                        </div>
                    </div>
                    {typeof errors.diplomaTotalLimit === 'string' && (
                        <p className="nord-demographics-error" role="alert">
                            {errors.diplomaTotalLimit}
                        </p>
                    )}
                </section>
            )}
        </Panel>
    );
};

export default NeglectedOrDelinquentHighSchoolDiplomaPanel;
